import React from 'react';
import { Box, Flex, Spinner, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import Card from '../../components/card/Card';
import LineChart from '../../components/charts/LineChart';
import BarChart from '../../components/charts/BarChart';

interface TrendDataPoint {
  timestamp: string;
  value: number;
}

interface TrendsData {
  signups: TrendDataPoint[];
  playbackHours: TrendDataPoint[];
  errors: TrendDataPoint[];
}

function toCategories(points: TrendDataPoint[]): string[] {
  return points.map((d) =>
    new Date(d.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  );
}

export function TrendCharts() {
  const { t } = useTranslation();
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-metrics-trends'],
    queryFn: () => apiClient.get<TrendsData>('/admin/metrics/trends', { days: 30 }),
    refetchInterval: 5 * 60 * 1000,
  });

  if (isLoading) return <Spinner size="sm" />;
  if (!data) return null;

  const signups = data.signups ?? [];
  const playbackHours = data.playbackHours ?? [];
  const errors = data.errors ?? [];

  const signupChartData = [{ name: 'Signups', data: signups.map((d) => d.value) }];
  const signupOptions: ApexCharts.ApexOptions = {
    xaxis: { categories: toCategories(signups) },
  };

  const playbackChartData = [{ name: 'Playback Hours', data: playbackHours.map((d) => d.value) }];
  const playbackOptions: ApexCharts.ApexOptions = {
    xaxis: { categories: toCategories(playbackHours) },
    colors: ['#39B8FF'],
  };

  const errorChartData = [{ name: 'Errors', data: errors.map((d) => d.value) }];
  const errorOptions: ApexCharts.ApexOptions = {
    xaxis: { categories: toCategories(errors) },
    colors: ['#ff7300'],
  };

  return (
    <Flex direction="column" gap="20px" data-testid="trend-charts">
      {/* Signup Trend */}
      <Card p="20px">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4px">
          {t('admin.dashboard.signupTrend')}
        </Text>
        <Box h="250px">
          <LineChart chartData={signupChartData} chartOptions={signupOptions} />
        </Box>
      </Card>

      {/* Playback Trend */}
      <Card p="20px">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4px">
          {t('admin.dashboard.playbackTrend')}
        </Text>
        <Box h="250px">
          <BarChart chartData={playbackChartData} chartOptions={playbackOptions} />
        </Box>
      </Card>

      {/* Error Trend */}
      <Card p="20px">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4px">
          {t('admin.dashboard.errorTrend')}
        </Text>
        {errors.length > 0 ? (
          <Box h="250px">
            <LineChart chartData={errorChartData} chartOptions={errorOptions} />
          </Box>
        ) : (
          <Text color={textColorSecondary} fontSize="sm" fontStyle="italic">
            {t('admin.dashboard.errorTrendComingSoon')}
          </Text>
        )}
      </Card>
    </Flex>
  );
}
